import { env } from '../env';
import { setStoredTokens, type StoredTokens } from './tokenStore';

/**
 * Office Dialog API-based PKCE login flow for Word / Excel / PowerPoint add-ins.
 *
 * Why not auth0-spa-js's loginWithPopup?
 *   Office WebViews (WKWebView on Mac, WebView2 on Windows) block `window.open`
 *   and return null. The SDK throws "Unable to open a popup for loginWithPopup".
 *
 * Why not loginWithRedirect?
 *   A full-page redirect would replace the task pane with Auth0's login UI,
 *   which Office then treats as the add-in content. Messy UX.
 *
 * The Office Dialog API is Microsoft's official pattern:
 *   1. Open Auth0's /authorize page in an Office-managed modal dialog
 *   2. User authenticates; Auth0 redirects the dialog to our callback URL
 *   3. Callback page (still inside the dialog, now on our origin) posts the
 *      auth code back to the parent via Office.context.ui.messageParent
 *   4. Parent receives code, closes dialog, exchanges code for tokens at
 *      Auth0's /oauth/token endpoint using our PKCE code_verifier
 */
/**
 * Auth0 connection name. Omit to let the user pick on Auth0's Universal Login page
 * (shows both Google and email/password, whichever is enabled on the tenant).
 *   - 'google-oauth2' → Google SSO, skips the chooser
 *   - 'Username-Password-Authentication' → email + password form, skips the chooser
 */
// Connection names vary per Auth0 tenant (staging student tenant uses
// 'Username-Password-Authentication'; prod dev-nyayassist tenant uses
// 'NyayAssist'), so this is a plain string sourced from env rather than a
// fixed literal union. Requesting a name not enabled on the client yields
// Auth0's "the connection is not enabled" error.
export type AuthConnection = string;

export async function loginViaOfficeDialog(
  options: { connection?: AuthConnection } = {},
): Promise<StoredTokens> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await pkceChallenge(codeVerifier);
  const state = crypto.randomUUID();
  const nonce = crypto.randomUUID();

  const authorizeUrl = buildAuthorizeUrl({
    codeChallenge,
    state,
    nonce,
    connection: options.connection,
  });
  const code = await openDialogAndGetCode(authorizeUrl);
  const tokens = await exchangeCodeForTokens({ code, codeVerifier });

  setStoredTokens(tokens);
  return tokens;
}

interface AuthorizeParams {
  codeChallenge: string;
  state: string;
  nonce: string;
  connection?: AuthConnection;
}

function buildAuthorizeUrl({ codeChallenge, state, nonce, connection }: AuthorizeParams): string {
  const url = new URL(`https://${env.auth0.domain}/authorize`);
  url.searchParams.set('client_id', env.auth0.clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', `${env.origin}/auth-callback.html`);
  url.searchParams.set('audience', env.auth0.audience);
  url.searchParams.set('scope', 'openid profile email offline_access');
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  url.searchParams.set('nonce', nonce);
  // Force Auth0 to show the login screen, never the signup screen. Signup
  // requires phone verification + profile onboarding flows that only work
  // on the full-page web app, so we deliberately keep it there. If a user
  // without an account tries to sign in, they'll hit the "Account not
  // found" state in AuthGate which links them to app.nyayassist.ai.
  url.searchParams.set('screen_hint', 'login');
  if (connection) {
    url.searchParams.set('connection', connection);
    if (connection === env.auth0.googleConnection) {
      // Force the Google account picker on re-auth so the user can switch accounts.
      // Not relevant for the DB connection — Auth0 handles re-auth via cookies.
      url.searchParams.set('prompt', 'select_account');
    }
  }
  return url.toString();
}

function openDialogAndGetCode(authorizeUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      authorizeUrl,
      { height: 60, width: 40, displayInIframe: false, promptBeforeOpen: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error?.message ?? 'Office dialog failed to open'));
          return;
        }
        const dialog = result.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          const messageArg = arg as { message?: string };
          try {
            const payload = JSON.parse(messageArg.message ?? '{}');
            dialog.close();
            if (payload.error) {
              reject(new Error(String(payload.error_description ?? payload.error)));
            } else if (payload.code) {
              resolve(String(payload.code));
            } else {
              reject(new Error('Auth dialog returned no code'));
            }
          } catch (err) {
            reject(err instanceof Error ? err : new Error('Failed to parse dialog message'));
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
          const eventArg = arg as { error?: number };
          // 12006 = user closed the dialog manually
          if (eventArg.error === 12006) {
            reject(new Error('Sign in cancelled'));
          } else if (eventArg.error) {
            reject(new Error(`Dialog error ${eventArg.error}`));
          }
        });
      },
    );
  });
}

async function exchangeCodeForTokens({
  code,
  codeVerifier,
}: {
  code: string;
  codeVerifier: string;
}): Promise<StoredTokens> {
  const res = await fetch(`https://${env.auth0.domain}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      client_id: env.auth0.clientId,
      code_verifier: codeVerifier,
      code,
      redirect_uri: `${env.origin}/auth-callback.html`,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token exchange failed (${res.status}): ${text}`);
  }
  const body = (await res.json()) as {
    access_token: string;
    refresh_token?: string;
    id_token: string;
    expires_in: number;
    scope: string;
  };
  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    idToken: body.id_token,
    expiresAt: Date.now() + body.expires_in * 1000,
    scope: body.scope,
  };
}

/**
 * Refresh an access token using a stored refresh token (rotation-compatible).
 * Called by the API client when the access token is about to expire.
 */
export async function refreshAccessToken(refreshToken: string): Promise<StoredTokens> {
  const res = await fetch(`https://${env.auth0.domain}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'refresh_token',
      client_id: env.auth0.clientId,
      refresh_token: refreshToken,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token refresh failed (${res.status}): ${text}`);
  }
  const body = (await res.json()) as {
    access_token: string;
    refresh_token?: string;
    id_token: string;
    expires_in: number;
    scope: string;
  };
  const tokens: StoredTokens = {
    accessToken: body.access_token,
    // Auth0 refresh rotation returns a new refresh token — use it if present,
    // otherwise keep the old one alive.
    refreshToken: body.refresh_token ?? refreshToken,
    idToken: body.id_token,
    expiresAt: Date.now() + body.expires_in * 1000,
    scope: body.scope,
  };
  setStoredTokens(tokens);
  return tokens;
}

// ---------- PKCE helpers ----------

function generateCodeVerifier(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return base64UrlEncode(bytes);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
